const API_BASE = "/api";

/** Safe UUID that works on non-secure origins (local IP, HTTP) where crypto.randomUUID is unavailable */
export function randomId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

export type ApiResponse<T = unknown> =
  | { ok: true; data: T }
  | { ok: false; error: string };

async function request<T>(
  method: string,
  path: string,
  body?: unknown
): Promise<ApiResponse<T>> {
  try {
    const headers: Record<string, string> = {};
    let bodyStr: string | undefined;
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
      bodyStr = JSON.stringify(body);
    }

    const res = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: "include",
      headers,
      body: bodyStr,
    });

    const contentType = res.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      return { ok: false, error: `Unexpected response (HTTP ${res.status}, ${contentType || "no content-type"})` };
    }

    const json = (await res.json()) as {
      ok: boolean;
      data?: T;
      error?: string;
    };

    if (json.ok) {
      return { ok: true, data: json.data as T };
    }
    return { ok: false, error: json.error ?? "Unknown error" };
  } catch (err) {
    return { ok: false, error: `Network error: ${err instanceof Error ? err.message : String(err)}` };
  }
}

export const api = {
  get<T = unknown>(path: string): Promise<ApiResponse<T>> {
    return request<T>("GET", path);
  },
  post<T = unknown>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return request<T>("POST", path, body);
  },
  put<T = unknown>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return request<T>("PUT", path, body);
  },
  patch<T = unknown>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return request<T>("PATCH", path, body);
  },
  del<T = unknown>(path: string): Promise<ApiResponse<T>> {
    return request<T>("DELETE", path);
  },
  /** multipart/form-data upload — caller provides the path and field name. */
  async upload<T = unknown>(path: string, fieldName: string, file: File): Promise<ApiResponse<T>> {
    try {
      const fd = new FormData();
      fd.append(fieldName, file);
      const res = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const contentType = res.headers.get("content-type") ?? "";
      if (!contentType.includes("application/json")) {
        return { ok: false, error: `Unexpected response (HTTP ${res.status})` };
      }
      const json = (await res.json()) as { ok: boolean; data?: T; error?: string };
      if (json.ok) return { ok: true, data: json.data as T };
      return { ok: false, error: json.error ?? "Upload failed" };
    } catch (err) {
      return { ok: false, error: `Network error: ${err instanceof Error ? err.message : String(err)}` };
    }
  },
};
